import { GridLegacy as Grid, Typography, Box, Slider } from '@mui/material'
import { C1Button, C1LoadingButton } from 'c1ui'
import { C1Dialog, C1DialogContent, C1DialogTitle, C1DialogActions } from 'components/core/dialog/C1Dialog'
import { Dropzone } from 'components/core/dropzone'
import { APIError } from 'components/core/errors/api'
import { useAlert } from 'hooks/useAlert'
import { useImageProcessor, uploadToCreateReturn } from 'hooks/useImageProcessor'
import { CropArea, cropToPng } from 'lib/crop-image'
import { App, SetAppIconRequest } from 'pbts/c1/api/app/v1/app'
import { FC, useCallback, useEffect } from 'react'
import Cropper from 'react-easy-crop'
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'
import { setAppIconApi } from 'redux_toolkit/app-icon'
import { SerializedErrorType } from 'redux_toolkit/lib/reducer'

// Custom hook for form submission
const useAppIconSubmission = (appId: string | undefined): {
    submitIcon: (file: File, croppedArea: CropArea) => Promise<boolean>
    inProgress: boolean
    apiError: SerializedErrorType | null
} => {
    const intl = useIntl()
    const alert = useAlert()
    const {
        mutate: setAppIcon,
        inProgress,
        error: apiError,
    } = setAppIconApi()

    const submitIcon = useCallback(async (file: File, croppedArea: CropArea): Promise<boolean> => {
        if (!appId) return false

        try {
            const imageBlob = await cropToPng(file, croppedArea)
            const arrayBuffer = await imageBlob.arrayBuffer()
            const bytes = new Uint8Array(arrayBuffer)

            const request = SetAppIconRequest.create({
                appId,
                data: bytes,
            })

            await setAppIcon(request)

            alert(
                intl.formatMessage({
                    id: 'app_icon_upload_success',
                    defaultMessage: 'App icon updated successfully',
                }),
                'success',
            )
            return true
        } catch (error) {
            console.error('Failed to save app icon:', error)
            return false
        }
    }, [appId, setAppIcon, alert, intl])

    return {
        submitIcon,
        inProgress,
        apiError: apiError || null,
    }
}

export const AppIconUpdateDialog: FC<{
    app: App | undefined,
    close: () => void,
}> = ({ app, close }): React.ReactElement => {
    const intl = useIntl()
    const alert = useAlert()

    // Custom hooks for separation of concerns
    const imageProcessor = useImageProcessor({
        errorMessageIds: {
            fileSize: 'app_icon_upload_file_size_error',
            fileType: 'app_icon_upload_file_type_error',
        },
    })
    const { submitIcon, inProgress, apiError } = useAppIconSubmission(app?.id)

    const handleClose = useCallback((): void => {
        if (inProgress) return // Prevent closing during API call

        imageProcessor.reset()
        close()
    }, [inProgress, imageProcessor, close])

    const handleSave = useCallback(async (): Promise<void> => {
        if (!imageProcessor.uploadedFile || !imageProcessor.croppedArea) {
            return
        }

        const success = await submitIcon(imageProcessor.uploadedFile, imageProcessor.croppedArea)
        if (success) {
            handleClose()
        }
    }, [imageProcessor.uploadedFile, imageProcessor.croppedArea, submitIcon, handleClose])

    const canSave = imageProcessor.uploadedFile !== null &&
        imageProcessor.upload.status === 'ok' &&
        imageProcessor.croppedArea !== null &&
        !inProgress

    // Handle API error
    useEffect(() => {
        if (apiError) {
            alert(
                intl.formatMessage({
                    id: 'app_icon_upload_error',
                    defaultMessage: 'Failed to update app icon: {error}',
                }, {
                    error: apiError.message || 'Unknown error',
                }),
                'error',
            )
        }
    }, [apiError, intl, alert])

    return (
        <C1Dialog
            open
            onClose={handleClose}
            maxWidth="md"
            aria-labelledby="app-icon-update-title"
            aria-describedby="app-icon-update-description"
        >
            <C1DialogTitle>
                <Typography id="app-icon-update-title" variant="h6" component="h2">
                    <FormattedMessage
                        id="app_icon_update_title"
                        defaultMessage="Update app icon"
                    />
                </Typography>
            </C1DialogTitle>
            <C1DialogContent>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography id="app-icon-update-description">
                            <FormattedMessage
                                id="app_icon_update_description"
                                defaultMessage="Upload and crop an image to use as your app icon. The image will be automatically converted to a square format."
                            />
                        </Typography>
                    </Grid>

                    {imageProcessor.upload.status === 'error' && (
                        <Grid item xs={12}>
                            <APIError
                                err={imageProcessor.upload.error}
                                title={
                                    <FormattedMessage
                                        id="app_icon_file_validation_error"
                                        defaultMessage="Invalid file format or size"
                                    />
                                }
                            />
                        </Grid>
                    )}

                    {apiError && (
                        <Grid item xs={12}>
                            <APIError
                                err={apiError}
                                title={
                                    <FormattedMessage
                                        id="app_icon_upload_error"
                                        defaultMessage="Failed to update app icon"
                                    />
                                }
                            />
                        </Grid>
                    )}

                    {!imageProcessor.uploadedFile && (
                        <Grid item xs={12}>
                            <Dropzone
                                onDrop={imageProcessor.handleFileDrop}
                                createReturn={uploadToCreateReturn(imageProcessor.upload)}
                            />
                        </Grid>
                    )}

                    {imageProcessor.uploadedFile && imageProcessor.imageUrl && (
                        <>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    <FormattedMessage
                                        id="app_icon_crop_instructions"
                                        defaultMessage="Drag to move the crop area and use the slider to zoom. The icon will be cropped to a square."
                                    />
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Box
                                    sx={{
                                        position: 'relative',
                                        height: 300,
                                        width: 300,
                                        backgroundColor: 'grey.100',
                                    }}
                                >
                                    <Cropper
                                        image={imageProcessor.imageUrl}
                                        crop={imageProcessor.crop}
                                        zoom={imageProcessor.zoom}
                                        aspect={1}
                                        onCropChange={imageProcessor.setCrop}
                                        onZoomChange={imageProcessor.setZoom}
                                        onCropComplete={imageProcessor.onCropComplete}
                                        style={{
                                            containerStyle: {
                                                width: '100%',
                                                height: '100%',
                                                backgroundColor: 'grey.100',
                                            },
                                        }}
                                    />
                                </Box>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="body2" gutterBottom>
                                    <FormattedMessage
                                        id="app_icon_zoom_label"
                                        defaultMessage="Zoom"
                                    />
                                </Typography>
                                <Slider
                                    value={imageProcessor.zoom}
                                    min={1}
                                    max={3}
                                    step={0.1}
                                    onChange={(_, value): void => imageProcessor.setZoom(value as number)}
                                    valueLabelDisplay="auto"
                                    aria-label={intl.formatMessage({
                                        id: 'app_icon_zoom_slider_aria',
                                        defaultMessage: 'Image zoom control',
                                    })}
                                />
                            </Grid>

                            {imageProcessor.uploadedFile && (
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">
                                        <FormattedMessage
                                            id="app_icon_file_info"
                                            defaultMessage="File: {file_name} ({file_size} KB)"
                                            values={{
                                                file_name: imageProcessor.uploadedFile.name,
                                                file_size: <FormattedNumber
                                                    value={imageProcessor.uploadedFile.size / 1024}
                                                    maximumFractionDigits={1}
                                                />,
                                            }}
                                        />
                                    </Typography>
                                </Grid>
                            )}
                        </>
                    )}
                </Grid>
            </C1DialogContent>
            <C1DialogActions>
                <C1Button
                    onClick={handleClose}
                    variant="outlined"
                    disabled={inProgress}
                    aria-label={intl.formatMessage({
                        id: 'app_icon_update_cancel_aria',
                        defaultMessage: 'Cancel icon update',
                    })}
                >
                    <FormattedMessage
                        id="app_icon_update_cancel"
                        defaultMessage="Cancel"
                    />
                </C1Button>
                <C1LoadingButton
                    variant="contained"
                    disabled={!canSave}
                    loading={inProgress}
                    onClick={handleSave}
                    aria-label={intl.formatMessage({
                        id: 'app_icon_update_save_aria',
                        defaultMessage: 'Save app icon',
                    })}
                >
                    <FormattedMessage
                        id="app_icon_update_save"
                        defaultMessage="Save icon"
                    />
                </C1LoadingButton>
            </C1DialogActions>
        </C1Dialog>
    )
}
